import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";

type CountResult = {
  table: string;
  count: number | null;
  error: string | null;
};

function getProjectRef(url: string): string | null {
  const match = url.match(/^https:\/\/([^.]+)\.supabase\.co/i);
  return match?.[1] ?? null;
}

export default function DbDebug() {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || "";
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || "";
  const commitHash = import.meta.env.VITE_COMMIT_HASH || "";

  const projectRef = useMemo(() => getProjectRef(supabaseUrl), [supabaseUrl]);

  const [results, setResults] = useState<CountResult[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      setLoading(true);
      const tables = ["leagues", "seasons", "rounds", "submissions", "votes", "users"];

      const next: CountResult[] = [];
      for (const table of tables) {
        try {
          const { count, error } = await supabase.from(table).select("id", { count: "exact", head: true });
          next.push({
            table,
            count: typeof count === "number" ? count : null,
            error: error ? error.message : null,
          });
        } catch (e) {
          next.push({
            table,
            count: null,
            error: e instanceof Error ? e.message : "Unknown error",
          });
        }
      }

      if (!cancelled) {
        setResults(next);
        setLoading(false);
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, []);

  const isConfigured =
    !!supabaseUrl &&
    !!anonKey &&
    supabaseUrl !== "https://placeholder.supabase.co" &&
    anonKey !== "placeholder-key";

  return (
    <div className="min-h-screen bg-background text-foreground p-6">
      <div className="max-w-3xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-semibold">DB Connection Debug</h1>
          <p className="text-sm text-muted-foreground">
            Use this page to confirm which Supabase project the site is calling and whether tables are readable.
          </p>
        </div>

        <div className="rounded-lg border p-4 space-y-2">
          <div className="text-sm">
            <span className="font-medium">Supabase configured:</span>{" "}
            <span className={isConfigured ? "text-green-500" : "text-red-500"}>{isConfigured ? "YES" : "NO"}</span>
          </div>
          <div className="text-sm break-all">
            <span className="font-medium">VITE_SUPABASE_URL:</span> {supabaseUrl || "(missing)"}
          </div>
          <div className="text-sm">
            <span className="font-medium">Project ref:</span> {projectRef || "(unknown)"}
          </div>
          <div className="text-sm">
            <span className="font-medium">Anon key present:</span> {anonKey ? `YES (…${anonKey.slice(-6)})` : "NO"}
          </div>
          <div className="text-sm">
            <span className="font-medium">Build commit:</span> {commitHash || "(unknown)"}
          </div>
        </div>

        <div className="rounded-lg border p-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-medium">Table counts</h2>
            <div className="text-sm text-muted-foreground">{loading ? "Loading…" : ""}</div>
          </div>

          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b">
                  <th className="py-2 pr-4">Table</th>
                  <th className="py-2 pr-4">Count</th>
                  <th className="py-2">Error</th>
                </tr>
              </thead>
              <tbody>
                {results.map((r) => (
                  <tr key={r.table} className="border-b last:border-b-0 align-top">
                    <td className="py-2 pr-4 font-medium">{r.table}</td>
                    <td className="py-2 pr-4">{r.count ?? "—"}</td>
                    <td className="py-2 text-red-500">{r.error ?? ""}</td>
                  </tr>
                ))}
                {results.length === 0 && !loading ? (
                  <tr>
                    <td className="py-2 text-muted-foreground" colSpan={3}>
                      No results yet.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>

          <p className="mt-3 text-xs text-muted-foreground">
            If counts are null with errors like “permission denied” / “RLS”, the site is talking to Supabase but reads are
            blocked by policies.
          </p>
        </div>
      </div>
    </div>
  );
}

